import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { Formik, Form, Field } from "formik";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../Appstore/store";
import { UpdateBlockList, GetAllBlockList } from "../Features/Blocklist";
import * as Yup from "yup";
import { setIsLoading } from "../Features/loadingSlice";
import { toast } from "sonner";
import axiosInstance from "../api/axiosInstance";
import Select from "react-select";

interface UpdateBlockListProps {
  show: boolean;
  onHide: () => void;
  selectedId: number | null;
  currentBlockListData: { number: string; campaign_id: number[] } | null;
}

const validationSchema = Yup.object({
  number: Yup.string()
    .matches(
      /^\+1\d{10}$/,
      "Invalid phone number format. Must start with +1 and contain 10 digits without any spaces."
    )
    .required("Number is required"),
});

const UpdateBlockListModal: React.FC<UpdateBlockListProps> = ({
  show,
  onHide,
  selectedId,
  currentBlockListData,
}) => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (show) {
      dispatch(setIsLoading(true));
      setTimeout(() => {
        dispatch(setIsLoading(false));
      }, 500);
    }
  }, [show, dispatch]);
  const [campaignOptions, setCampaignOptions] = useState<
    { value: number; label: string }[]
  >([]);

  const getAllCampaign = () => {
    axiosInstance
      .get("/api/admin/sms-campaign-list")
      .then(({ data }) => {
        setCampaignOptions(
          data?.campaign.map((item: any) => ({
            value: item.id,
            label: item.name,
          })) || []
        );
      })
      .catch(() => {});
  };

  const initialValues: { number: string; campaign_id: number[] } = {
    number: currentBlockListData ? currentBlockListData.number : "",
    campaign_id: currentBlockListData ? currentBlockListData.campaign_id : [],
  };

  useEffect(() => {
    getAllCampaign();
  }, []);
  return (
    <>
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title>Edit Block List</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            enableReinitialize={true}
            onSubmit={async (values, { resetForm }) => {
              if (selectedId === null) {
                console.error("No ID provided for update.");
                return;
              }

              const blocklistData = {
                number: values.number,
                campaign_id: values.campaign_id,
              };
              try {
                const response: any = await dispatch(
                  UpdateBlockList({ id: selectedId, blocklistData })
                );

                if (response.payload && response.payload.status === "success") {
                  toast.success(response.payload.message);
                  resetForm();
                  onHide();
                  await dispatch(
                    GetAllBlockList({ search: "", page: 1, limit: 10 })
                  );
                } else if (response.payload?.status === "error") {
                  const backendErrorMessage =
                    response.payload.message || "Invalid credentials";
                  toast.error(backendErrorMessage);
                }
              } catch (error: any) {
                const errorMessage =
                  error.message || "An unexpected error occurred";
                toast.error(errorMessage);
              }
            }}
          >
            {({
              errors,
              touched,
              handleChange,
              values,
              isSubmitting,
              handleSubmit,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                <div className="form-group mb-3">
                  <label htmlFor="number">
                    Number<span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control shadow-none`}
                    id="number"
                    name="number"
                    placeholder="Enter a Number"
                    onChange={handleChange}
                    value={values.number}
                  />
                  {touched.number && errors.number ? (
                    <div className="text-danger">{errors.number}</div>
                  ) : null}
                </div>

                <div className="form-group mb-3">
                  <label htmlFor="number">
                    Select Campaign<span className="text-danger">*</span>
                  </label>
                  <Select
                    styles={{
                      option: (styles) => {
                        return {
                          ...styles,
                          backgroundCategory: "white",
                          category: "#000",
                          ":hover": {
                            backgroundCategory: "#f5f5f5",
                          },
                        };
                      },
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999,
                      }),
                    }}
                    isMulti={true}
                    value={campaignOptions.filter((item) =>
                      values.campaign_id.includes(item.value)
                    )}
                    options={
                      campaignOptions.length === values.campaign_id.length
                        ? campaignOptions
                        : [
                            { value: 0, label: "Select All" },
                            ...campaignOptions,
                          ]
                    }
                    isSearchable={true}
                    onChange={(e: any) => {
                      if (e.map((item: any) => item.value).includes(0))
                        setFieldValue(
                          "campaign_id",
                          campaignOptions.map((item: any) => item.value)
                        );
                      else
                        setFieldValue(
                          "campaign_id",
                          e.map((item: any) => item.value)
                        );
                    }}
                    placeholder="Please select campaign"
                  />
                  {touched.campaign_id && errors.campaign_id ? (
                    <div className="text-danger">{errors.campaign_id}</div>
                  ) : null}
                </div>

                <Modal.Footer className="d-flex justify-content-center border-0">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        <span className="ps-2">Submitting...</span>
                      </>
                    ) : (
                      "Submit"
                    )}
                  </Button>
                  <Button
                    style={{ width: 76.5 }}
                    variant="secondary"
                    onClick={onHide}
                  >
                    Close
                  </Button>
                </Modal.Footer>
              </form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default UpdateBlockListModal;
